import { create } from 'zustand';
import {
  parseRemedyString,
  validate,
  formatRemedy,
  RemedyParseError,
  type CompiledArtifact,
  type Issue,
  type RemedyDoc,
} from '@petitgen/remedy-core/browser';

export type View = 'dashboard' | 'editor' | 'constellation' | 'lint' | 'build';

export interface OpenFolder {
  /** Human label (file.name for uploads, handle.name for FS Access). */
  name: string;
  /** File System Access API directory handle — undefined for single-file uploads. */
  dirHandle?: FileSystemDirectoryHandle;
  /** File handle for the .remedy file, when we have one. */
  fileHandle?: FileSystemFileHandle;
}

export interface BuildState {
  status: 'idle' | 'running' | 'done' | 'error';
  progress: { done: number; total: number; path: string } | null;
  artifact: CompiledArtifact | null;
  error: string | null;
  /** The last artifact we cached, for incremental rebuilds. */
  priorArtifact: CompiledArtifact | null;
}

interface State {
  view: View;
  setView: (v: View) => void;

  folder: OpenFolder | null;
  source: string;
  fileName: string | null;
  doc: RemedyDoc | null;
  canonical: string | null;
  errors: Issue[];
  warnings: Issue[];
  parseError: string | null;

  setSource: (source: string, fileName?: string, folder?: OpenFolder) => void;
  canonicalise: () => void;
  clear: () => void;

  build: BuildState;
  setBuildState: (patch: Partial<BuildState>) => void;
}

function analyse(source: string) {
  if (source.trim() === '') {
    return {
      doc: null,
      canonical: null,
      errors: [] as Issue[],
      warnings: [] as Issue[],
      parseError: null as string | null,
    };
  }
  try {
    const doc = parseRemedyString(source);
    const result = validate(doc);
    const canonical = formatRemedy(doc);
    return {
      doc,
      canonical,
      errors: result.errors,
      warnings: result.warnings,
      parseError: null as string | null,
    };
  } catch (e) {
    const msg = e instanceof RemedyParseError ? e.message : (e as Error).message;
    return { doc: null, canonical: null, errors: [], warnings: [], parseError: msg };
  }
}

export const useStore = create<State>((set, get) => ({
  view: 'dashboard',
  setView: (view) => set({ view }),

  folder: null,
  source: '',
  fileName: null,
  ...analyse(''),

  setSource: (source, fileName, folder) => {
    const a = analyse(source);
    set({
      source,
      fileName: fileName ?? get().fileName,
      folder: folder ?? get().folder,
      ...a,
      view: source ? 'editor' : 'dashboard',
    });
  },

  canonicalise: () => {
    const { canonical } = get();
    if (canonical) get().setSource(canonical);
  },

  clear: () =>
    set({
      folder: null,
      source: '',
      fileName: null,
      doc: null,
      canonical: null,
      errors: [],
      warnings: [],
      parseError: null,
      view: 'dashboard',
    }),

  build: {
    status: 'idle',
    progress: null,
    artifact: null,
    error: null,
    priorArtifact: null,
  },
  setBuildState: (patch) => set((s) => ({ build: { ...s.build, ...patch } })),
}));

/**
 * Subscribe-friendly selectors. Using these prevents unnecessary rerenders.
 */
export const selectSourceState = (s: State) => ({
  source: s.source,
  fileName: s.fileName,
  doc: s.doc,
  canonical: s.canonical,
  errors: s.errors,
  warnings: s.warnings,
  parseError: s.parseError,
});
